'use client';

import { useLocalPreferenceSelector } from '@shared/utils/context/LocalPreferenceContext';
import { useLocalPreferenceActions } from '@shared/utils/hooks/useLocalPreference';
import classNames from 'classnames';
import {
  Maximize2,
  Minimize2,
  Pause,
  Play,
  RotateCcw,
  Volume2,
  VolumeX
} from 'lucide-react';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';

type VideoPlayerProps = {
  src: string;
  poster?: string;
  className?: string;
  loop?: boolean;
  controls?: boolean;
  playsInline?: boolean;
  onPlay?: () => void;
  onPause?: () => void;
  onMute?: (muted: boolean) => void;
  preload?: 'auto' | 'metadata' | 'none';
  style?: React.CSSProperties;
  muted?: boolean;
  onMouseOver?: (e: React.MouseEvent<HTMLVideoElement>) => void;
  onClick?: (e: React.MouseEvent<HTMLVideoElement>) => void;
  autoPlay?: boolean;
  videoSize?: {
    width: number;
    height: number;
  };
  // Fill the parent box (parent controls size) instead of the intrinsic
  // aspect-video box — used by ViewPostMedia's fixed stage.
  fill?: boolean;
  bufferingIndicator?: React.ReactNode;
  deferLoadUntilClick?: boolean;
};

// 👇 forwardRef so parent can control the <video />
const VideoPlayer = forwardRef<HTMLVideoElement, VideoPlayerProps>(
  (
    {
      src,
      poster,
      loop = false,
      controls = true,
      playsInline = true,
      onPlay,
      onPause,
      onMute,
      preload = 'metadata',
      style,
      autoPlay = false,
      muted = false,
      onMouseOver,
      onClick,
      className,
      videoSize,
      fill = false,
      bufferingIndicator,
      deferLoadUntilClick = false
    },
    ref
  ) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [showControls, setShowControls] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isHydrated, setIsHydrated] = useState(false);
    const [isBuffering, setIsBuffering] = useState(false);
    const [hasStartedLoading, setHasStartedLoading] = useState(
      !deferLoadUntilClick || autoPlay
    );
    const [showTempIcon, setShowTempIcon] = useState(false);
    const [tempIconType, setTempIconType] = useState<'play' | 'pause'>('play');

    const internalVideoRef = useRef<HTMLVideoElement>(null);
    const videoContainerRef = useRef<HTMLDivElement>(null);
    const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
      null
    );
    const tempIconTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
      null
    );

    const { updateMuteVideo } = useLocalPreferenceActions();
    const muteVideo = useLocalPreferenceSelector((state) => state.muteVideo);

    // 👇 expose the <video> element to parent
    useImperativeHandle(ref, () => internalVideoRef.current!);

    // Hydration
    useEffect(() => setIsHydrated(true), []);

    useEffect(() => {
      setHasStartedLoading(!deferLoadUntilClick || autoPlay);
      setIsBuffering(false);
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
    }, [src, deferLoadUntilClick, autoPlay]);

    // Fullscreen listener
    useEffect(() => {
      const handleFullscreenChange = () =>
        setIsFullscreen(Boolean(document.fullscreenElement));
      document.addEventListener('fullscreenchange', handleFullscreenChange);
      return () =>
        document.removeEventListener(
          'fullscreenchange',
          handleFullscreenChange
        );
    }, []);

    // Init video
    useEffect(() => {
      if (isHydrated && internalVideoRef.current) {
        setCurrentTime(0);
        setDuration(0);
        setIsPlaying(false);
        if (internalVideoRef.current.readyState >= 1) {
          setDuration(internalVideoRef.current.duration);
        }
      }
    }, [isHydrated]);

    // Cleanup temp icon timeout
    useEffect(() => {
      const fullscreenChange = () => {
        if (document.fullscreenElement) return;
        if (internalVideoRef.current) {
          internalVideoRef.current.controls = true;
          internalVideoRef.current.controls = false;
        }
      };
      document.addEventListener('fullscreenchange', fullscreenChange);
      return () => {
        if (tempIconTimeoutRef.current)
          clearTimeout(tempIconTimeoutRef.current);
        document.removeEventListener('fullscreenchange', fullscreenChange);
      };
    }, []);

    const showTemporaryIcon = (type: 'play' | 'pause') => {
      if (tempIconTimeoutRef.current) clearTimeout(tempIconTimeoutRef.current);
      setTempIconType(type);
      setShowTempIcon(true);
      tempIconTimeoutRef.current = setTimeout(
        () => setShowTempIcon(false),
        1000
      );
    };

    const handlePlayPause = () => {
      if (!internalVideoRef.current) return;
      if (!hasStartedLoading) {
        setHasStartedLoading(true);
      }
      if (isPlaying) {
        internalVideoRef.current.pause();
        showTemporaryIcon('pause');
      } else {
        internalVideoRef.current.play();
        showTemporaryIcon('play');
      }
      setIsPlaying(!isPlaying);
    };

    const handleControlPlayPause = () => {
      if (!hasStartedLoading) {
        setHasStartedLoading(true);
        requestAnimationFrame(() => {
          internalVideoRef.current?.play();
        });
        return;
      }
      if (!internalVideoRef.current) return;
      if (isPlaying) internalVideoRef.current.pause();
      else internalVideoRef.current.play();
      setIsPlaying(!isPlaying);
    };

    const handleMuteToggle = () => {
      if (!internalVideoRef.current) return;
      const newMutedState = !muteVideo;
      internalVideoRef.current.muted = newMutedState;
      updateMuteVideo(newMutedState);
      onMute?.(newMutedState);
    };

    const handleTimeUpdate = () => {
      if (internalVideoRef.current)
        setCurrentTime(internalVideoRef.current.currentTime);
    };

    const handleLoadedMetadata = () => {
      if (internalVideoRef.current)
        setDuration(internalVideoRef.current.duration);
    };

    const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
      const time = parseFloat(e.target.value);
      if (internalVideoRef.current && !isNaN(time)) {
        internalVideoRef.current.currentTime = time;
        setCurrentTime(time);
      }
    };

    const handleFullscreen = () => {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        // iOS Safari needs video element fullscreen
        const webkitVideo = internalVideoRef.current as
          | (HTMLVideoElement & { webkitEnterFullscreen?: () => void })
          | null;
        if (webkitVideo?.webkitEnterFullscreen) {
          webkitVideo.webkitEnterFullscreen();
        } else {
          videoContainerRef.current?.requestFullscreen();
        }
      }
    };

    const handleRestart = () => {
      if (internalVideoRef.current) {
        internalVideoRef.current.currentTime = 0;
        setCurrentTime(0);
      }
    };

    const formatTime = (time: number) => {
      const m = Math.floor(time / 60);
      const s = Math.floor(time % 60);
      return `${m}:${s.toString().padStart(2, '0')}`;
    };

    const handleMouseMove = () => {
      setShowControls(true);
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
      controlsTimeoutRef.current = setTimeout(() => {
        if (isPlaying) setShowControls(false);
      }, 3000);
    };

    const handleMouseLeave = (e: React.MouseEvent) => {
      const target = e.relatedTarget as HTMLElement | null;
      const isMovingToControls = target?.closest?.('[data-video-controls]');
      if (isPlaying && !isMovingToControls) setShowControls(false);
    };

    return (
      <div
        ref={videoContainerRef}
        className={classNames(
          'relative overflow-hidden bg-black shadow-2xl',
          isFullscreen
            ? 'h-screen w-screen rounded-none'
            : fill
              ? 'size-full rounded-lg'
              : videoSize
                ? 'rounded-2xl'
                : 'aspect-video rounded-2xl',
          className
        )}
        style={
          !isFullscreen && videoSize
            ? {
                width: videoSize.width,
                height: videoSize.height,
                maxWidth: '100%'
              }
            : undefined
        }
      >
        <div
          className={classNames(
            'relative',
            isFullscreen || videoSize || fill ? 'h-full w-full' : 'aspect-video'
          )}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onClick={handlePlayPause}
        >
          <video
            ref={internalVideoRef}
            style={style}
            className="size-full object-contain"
            poster={poster}
            loop={loop}
            playsInline={playsInline}
            src={hasStartedLoading ? src : undefined}
            preload={preload}
            autoPlay={autoPlay}
            muted={muted}
            onMouseOver={onMouseOver}
            onClick={onClick}
            onPlay={() => {
              setIsBuffering(false);
              setIsPlaying(true);
              onPlay?.();
            }}
            onPause={() => {
              setIsPlaying(false);
              onPause?.();
            }}
            onWaiting={() => setIsBuffering(true)}
            onPlaying={() => setIsBuffering(false)}
            onCanPlay={() => setIsBuffering(false)}
            onVolumeChange={() => {
              if (internalVideoRef.current) {
                const isMuted = internalVideoRef.current.muted;
                updateMuteVideo(isMuted);
                onMute?.(isMuted);
              }
            }}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            controls={false}
          >
            {hasStartedLoading && <source src={src} type="video/mp4" />}
            Your browser does not support the video tag.
          </video>

          {deferLoadUntilClick && !hasStartedLoading && (
            <button
              type="button"
              className="absolute inset-0 z-10 flex items-center justify-center bg-black/30 text-white backdrop-blur-sm"
              onClick={(e) => {
                e.stopPropagation();
                setHasStartedLoading(true);
                requestAnimationFrame(() => {
                  internalVideoRef.current?.play();
                });
              }}
            >
              {bufferingIndicator ?? (
                <div className="flex items-center gap-2 rounded-full bg-black/50 px-4 py-2 text-sm">
                  <Play className="size-4" fill="white" />
                  <span>Click to play</span>
                </div>
              )}
            </button>
          )}

          {hasStartedLoading && isBuffering && (
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
              {bufferingIndicator ?? (
                <div className="rounded-full bg-black/50 px-4 py-2 text-sm text-white">
                  Buffering...
                </div>
              )}
            </div>
          )}

          {/* Overlay Controls */}
          <div
            data-video-controls
            className={`pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent transition-opacity duration-300 ${showControls && controls ? 'opacity-100' : 'opacity-0'}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Temp Play/Pause icon */}
            {showTempIcon && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                {/* The old `exit` never ran — no AnimatePresence wrapped this,
                    so the icon always vanished the moment it was unmounted. */}
                <button className="anim-enter rounded-full bg-black/20 p-8 backdrop-blur-sm [--enter-scale:0]">
                  {tempIconType === 'play' ? (
                    <Play className="size-16 text-white" fill="white" />
                  ) : (
                    <Pause className="size-16 text-white" />
                  )}
                </button>
              </div>
            )}

            {/* Bottom Controls */}
            <div className="pointer-events-auto absolute inset-x-0 bottom-0 p-6">
              {/* Progress */}
              <div className="mb-4">
                {isHydrated && (
                  <input
                    type="range"
                    min="0"
                    max={duration || 0}
                    value={currentTime}
                    onChange={handleSeek}
                    step="0.1"
                    className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-white/20 transition-all hover:bg-white/30"
                    style={{
                      background: `linear-gradient(to right, white ${
                        duration > 0 ? (currentTime / duration) * 100 : 0
                      }%, rgba(255,255,255,0.2) ${
                        duration > 0 ? (currentTime / duration) * 100 : 0
                      }%)`
                    }}
                  />
                )}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {/* Play/Pause */}
                  <button
                    onClick={handleControlPlayPause}
                    className="rounded-full bg-white/20 p-2 backdrop-blur-sm transition-transform duration-200 hover:scale-[1.05] hover:bg-white/30 active:scale-[0.95]"
                  >
                    {isPlaying ? (
                      <Pause className="size-6 text-white" />
                    ) : (
                      <Play className="size-6 text-white" fill="white" />
                    )}
                  </button>

                  {/* Mute */}
                  <button
                    onClick={handleMuteToggle}
                    className="rounded-full bg-white/20 p-2 backdrop-blur-sm transition-transform duration-200 hover:scale-[1.05] hover:bg-white/30 active:scale-[0.95]"
                  >
                    {muteVideo ? (
                      <VolumeX className="size-6 text-white" />
                    ) : (
                      <Volume2 className="size-6 text-white" />
                    )}
                  </button>

                  {/* Restart */}
                  <button
                    onClick={handleRestart}
                    className="rounded-full bg-white/20 p-2 backdrop-blur-sm transition-transform duration-200 hover:scale-[1.05] hover:bg-white/30 active:scale-[0.95]"
                  >
                    <RotateCcw className="size-6 text-white" />
                  </button>

                  <span className="text-sm text-white">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>
                </div>

                {/* Fullscreen */}
                <button
                  onClick={handleFullscreen}
                  className="rounded-full bg-white/20 p-2 backdrop-blur-sm transition-transform duration-200 hover:scale-[1.05] hover:bg-white/30 active:scale-[0.95]"
                >
                  {isFullscreen ? (
                    <Minimize2 className="size-6 text-white" />
                  ) : (
                    <Maximize2 className="size-6 text-white" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

VideoPlayer.displayName = 'VideoPlayer';

export default VideoPlayer;
